import {validateSignupProfileConfig, type AuthDeploymentConfig} from "./deployment-config.js";
import {birthDateFromAge, normaliseDateOfBirth, parseAgeYears} from "./date-of-birth.js";
import {getUserNameValidationError, normaliseUserName} from "./user-name.js";

/** A display name is not a given/family-name pair; never invent a surname or a handle. */
export type SignupProfile =
    | {kind: "display-name"; displayName: string}
    | {kind: "first-last-username"; firstName: string; lastName: string; userName: string};
/** Forge (display-name) collects an AGE; TwoShot collects a date of birth. */
export type SignupInput = SignupProfile & {dateOfBirth?: string; age?: string; tosAccepted: boolean};
export type SignupProfileError = "display_name_required" | "first_and_last_name_required" | "invalid_name" | "invalid_username" | "date_of_birth_required" | "invalid_date_of_birth" | "age_required" | "invalid_age" | "terms_required";
export type ParsedSignupProfile =
    | {kind: "absent"}
    | {kind: "invalid"; error: SignupProfileError}
    | {kind: "present"; profile: SignupProfile; dateOfBirth: string; dateOfBirthPrecision: BirthDatePrecision};
/** "year": derived from a self-declared age (the youngest consistent date); "day": a full birthday. */
export type BirthDatePrecision = "day" | "year";
export const SIGNUP_NAME_MAX_LENGTH = 64;

export function cleanSignupName(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}
function validName(value: string): boolean {
    // Joiners are legitimate in human names (and emoji); standalone invisible
    // names, bidi overrides and other controls are not useful display data.
    const withoutJoiners = value.replace(/[\u200c\u200d]/g, "");
    return !!value && value.length <= SIGNUP_NAME_MAX_LENGTH
        && /[^\p{Z}\p{C}\p{M}]/u.test(value.replace(/\p{Default_Ignorable_Code_Point}/gu, ""))
        && !/[\p{Cc}\p{Cf}]/u.test(withoutJoiners);
}
function bodyString(body: Record<string, unknown>, key: string): string {
    return typeof body[key] === "string" ? body[key] as string : "";
}

/** Shared email/phone/fallback/OAuth parser. cfg is SERVER-owned; body.client_id has no role. */
export function parseSignupProfile(
    body: unknown,
    cfg: AuthDeploymentConfig,
    options: {providerNames?: {firstName: string; lastName: string}; now?: Date} = {},
): ParsedSignupProfile {
    validateSignupProfileConfig(cfg);
    const b = typeof body === "object" && body !== null ? body as Record<string, unknown> : {};
    const keys = ["display_name", "first_name", "last_name", "user_name", "date_of_birth", "age", "tos_accepted"];
    if (!keys.some(key => Object.prototype.hasOwnProperty.call(b, key))) return {kind: "absent"};
    const names = parseProfileFields(b, cfg, options.providerNames);
    if ("kind" in names) return names;
    const {profile} = names;
    // Forge (display-name) asks for an AGE, with a toggle for the full birthday; TwoShot only ever sends a
    // date. No minimum age here: admission is the owner's call. Only basic sanity (1-120 years, not future).
    let dateOfBirth: string | null;
    let dateOfBirthPrecision: BirthDatePrecision;
    if (cfg.signupProfile === "display-name" && b.age !== undefined) {
        if (b.age === "" || b.age === null) return {kind: "invalid", error: "age_required"};
        const years = parseAgeYears(b.age);
        if (years === null) return {kind: "invalid", error: "invalid_age"};
        dateOfBirth = normaliseDateOfBirth(birthDateFromAge(years, options.now), options.now);
        if (!dateOfBirth) return {kind: "invalid", error: "invalid_age"};
        dateOfBirthPrecision = "year";
    } else {
        const rawDateOfBirth = bodyString(b, "date_of_birth").trim();
        if (!rawDateOfBirth) {
            // Forge's default field is the age; it only sends date_of_birth when the birthday toggle is on.
            const forgeAgeMode = cfg.signupProfile === "display-name" && !Object.prototype.hasOwnProperty.call(b, "date_of_birth");
            return {kind: "invalid", error: forgeAgeMode ? "age_required" : "date_of_birth_required"};
        }
        dateOfBirth = normaliseDateOfBirth(rawDateOfBirth, options.now);
        if (!dateOfBirth) return {kind: "invalid", error: "invalid_date_of_birth"};
        dateOfBirthPrecision = "day";
    }
    if (b.tos_accepted !== true) return {kind: "invalid", error: "terms_required"};
    return {kind: "present", profile, dateOfBirth, dateOfBirthPrecision};
}

function parseProfileFields(b: Record<string, unknown>, cfg: AuthDeploymentConfig, providerNames?: {firstName: string; lastName: string}): {profile: SignupProfile} | {kind: "invalid"; error: SignupProfileError} {
    if (cfg.signupProfile === "display-name") {
        // Provider names are a prefill for the UI, not an assertion of what this person wants to be called.
        const displayName = cleanSignupName(bodyString(b, "display_name"));
        if (!displayName) return {kind: "invalid", error: "display_name_required"};
        if (!validName(displayName)) return {kind: "invalid", error: "invalid_name"};
        return {profile: {kind: "display-name", displayName}};
    } else {
        const firstName = cleanSignupName(b.first_name === undefined && providerNames
            ? providerNames.firstName : bodyString(b, "first_name"));
        const lastName = cleanSignupName(b.last_name === undefined && providerNames
            ? providerNames.lastName : bodyString(b, "last_name"));
        if (!firstName || !lastName) return {kind: "invalid", error: "first_and_last_name_required"};
        if (!validName(firstName) || !validName(lastName)) return {kind: "invalid", error: "invalid_name"};
        const userName = normaliseUserName(bodyString(b, "user_name"));
        if (getUserNameValidationError(userName)) return {kind: "invalid", error: "invalid_username"};
        return {profile: {kind: "first-last-username", firstName, lastName, userName}};
    }
}

/** One serialization for every SPA rail; the browser never generates the hidden handle. */
export function signupRequestBody(input: SignupInput): Record<string, unknown> {
    return {
        ...(input.kind === "display-name"
            ? {display_name: cleanSignupName(input.displayName)}
            : {first_name: cleanSignupName(input.firstName), last_name: cleanSignupName(input.lastName), user_name: normaliseUserName(input.userName)}),
        ...(input.kind === "display-name" && input.age !== undefined ? {age: input.age.trim()} : {date_of_birth: input.dateOfBirth ?? ""}),
        tos_accepted: input.tosAccepted,
    };
}

/** Why this input would be refused, or null when the server would accept it. */
export function signupInputProblem(input: SignupInput, cfg: AuthDeploymentConfig, now?: Date): SignupProfileError | null {
    if (input.kind !== cfg.signupProfile) return cfg.signupProfile === "display-name" ? "display_name_required" : "first_and_last_name_required";
    const parsed = parseSignupProfile(signupRequestBody(input), cfg, {now});
    return parsed.kind === "invalid" ? parsed.error : parsed.kind === "present" ? null : "terms_required";
}

export function signupInputIsValid(input: SignupInput, cfg: AuthDeploymentConfig, now?: Date): boolean {
    if (input.kind !== cfg.signupProfile) return false;
    return parseSignupProfile(signupRequestBody(input), cfg, {now}).kind === "present";
}

/** The standalone schema has no display_name column. Consumers join these two columns.
 * Store the complete chosen display name in first_name and NULL (unknown), not a fabricated surname. */
export function signupStorageProfile(profile: SignupProfile, cfg: AuthDeploymentConfig): {firstName: string; lastName: string | null; userName: string | null} {
    if (profile.kind !== cfg.signupProfile) throw new Error("invalid_signup_profile");
    validateSignupProfileConfig(cfg);
    const body = profile.kind === "display-name"
        ? {display_name: profile.displayName}
        : {first_name: profile.firstName, last_name: profile.lastName, user_name: profile.userName};
    const parsed = parseProfileFields(body, cfg);
    if ("kind" in parsed) throw new Error(parsed.error);
    return parsed.profile.kind === "display-name"
        ? {firstName: parsed.profile.displayName, lastName: null, userName: null}
        : {firstName: parsed.profile.firstName, lastName: parsed.profile.lastName, userName: parsed.profile.userName};
}
